import { IDetailInfo, IDocComplete } from "@/types/summary";
import { cloneDeep } from "lodash";
import { create } from "zustand";
import summaryCommonStore from "./summaryCommon.store";
import { editSrcContent, getMeetingDetail, getMeetingTitle, renameRecord } from "../services/summary";
import { getBackupSetting, getShareMeetingDetail, getShareMeetingTitle } from "@/services/share";
import { AxiosRequestConfig } from "axios";
import { formatOldDetail } from "@/utils/formatOldDetail";
import fridayReporter from "@/utils/friday";
import { MeetingRecordVisibility, Origin } from "@/constants/share";
interface SummarySrcState {
  detailInfo: IDetailInfo | null;
  recordEditing: boolean;
  newDetailInfo: IDetailInfo | null;
  newCompleteDoc: IDocComplete | null;
  srcLoading: boolean;
  titleGenerating: boolean;
  generatedTitle: string;
}

interface SummarySrcStore extends SummarySrcState {
  fetchDetailInfo: (param: { recordingType: string; streamId: string; fromShare?: boolean }, needThrowError?: boolean, config?: AxiosRequestConfig<unknown>) => Promise<IDetailInfo | undefined>;
  updateDetailInfo: () => void;
  setDetailInfo: (data: IDetailInfo) => void;
  setRecordEditing: (editing: boolean) => void;
  modifyDetailInfo: (newData: IDetailInfo) => Promise<void>;
  markSrcSummary: (paragraphId: string, sentenceId?: string) => void;
  submitSrcSummary: () => Promise<void>;
  modifyNewCompleteDoc: (newData: IDocComplete) => void;
  submitModifyTitle: (newTitle: string) => Promise<void>;
  setSrcLoading: (loading: boolean) => void;
  initStore: () => void;
  generateTitle: (reload?: boolean, streamName?: string) => Promise<void>;
  fetchAllowBackUpSetting: () => void;
}

const summarySrcState: SummarySrcState = {
  srcLoading: false,
  detailInfo: null,
  recordEditing: false,
  newDetailInfo: null,
  newCompleteDoc: null,
  titleGenerating: false,
  generatedTitle: "",
};

const summarySrcStore = create<SummarySrcStore>()((set, get) => ({
  ...summarySrcState,
  initStore: () => {
    set({ ...summarySrcState });
  },
  updateDetailInfo: async () => {
    const { fetchDetailInfo } = get();
    fetchDetailInfo({
      recordingType: "live",
      streamId: summaryCommonStore.getState().streamId,
    });
  },
  fetchDetailInfo: async (params, needThrowError = false, config) => {
  try {
    let res: IDetailInfo | undefined;
    const { shareInfo, setAudioScTimeList } = summaryCommonStore.getState();
    if (shareInfo && summaryCommonStore.getState().shareScope !== MeetingRecordVisibility.orgMember) {
      res = await getShareMeetingDetail({ ...params, ...shareInfo }, config);
    } else {
      res = await getMeetingDetail(params, config);
    }
    if (!res) {
      throw new Error("获取详情失败");
    }

    const streamId = summaryCommonStore.getState().streamId;
    if (!res.streamName.includes(streamId)) {
      return;
    }

    const scTimeList: number[] = [];
    res.srcDoc?.pg.forEach((item) => {
      item.roleName = item.roleName || item.role;
      item.sc.forEach((scItem) => {
        scTimeList.push(scItem.bt / 1000);
        scTimeList.push(scItem.et / 1000);
      });
    });
    const useComplete = shareInfo?.origin === Origin.MEETING_PLATE && shareInfo?.fileTransEnable;
    const targetContent = useComplete ? 'completeDoc' : 'srcDoc';
    const { fromShare } = params;
    // 如果!fromShare, 则以后面fetchAllowBackUpSetting返回结果为准
    const formatRes = { ...res, srcDoc: res[targetContent], allowBackUp: !fromShare ? false : res.allowBackUp }
    if (formatRes.srcDoc) {
      formatRes.srcDoc.pg = formatOldDetail(formatRes.srcDoc.pg, formatRes.originType)
    }
    set({
      detailInfo: formatRes,
      newCompleteDoc: cloneDeep(formatRes.srcDoc),
    });
    setAudioScTimeList(scTimeList);
    if (!fromShare) get().fetchAllowBackUpSetting();
    return res;
    } catch (error: any) {
      console.log('fetchDetailInfo error', error);
      // 录制信息已删除
      if (error?.errCode === 40002103) {
        summaryCommonStore.getState().setErrorStatus(40002103);
      }
      if (needThrowError) {
        throw error;
      }
    }
  },
  fetchAllowBackUpSetting: async () => {
    const { detailInfo } = get();
    if (!detailInfo) {
      return;
    }
    const res = await getBackupSetting({ streamName: detailInfo.streamName });
    set({ detailInfo: { ...detailInfo, allowBackUp: res } });
  },
  setDetailInfo: (data) => {
    set({ detailInfo: data });
  },
  setRecordEditing: (editing) => {
    if (editing) {
      const { modifyDetailInfo, detailInfo } = get();
      if (!detailInfo) {
        return;
      }
      modifyDetailInfo(detailInfo);
    }
    set({ recordEditing: editing });
  },
  modifyDetailInfo: async (newData) => {
    try {
      set({ newDetailInfo: newData });
    } catch (error) {
      console.error(error);
    }
  },
  // 有sentenceID则标记句子，否则标记段落
  markSrcSummary: (paragraphId, sentenceId) => {
    const { newCompleteDoc, submitSrcSummary } = get();
    if (!newCompleteDoc) {
      return;
    }
    const paragraph = newCompleteDoc.pg.find((item) => item.pi === paragraphId);
    if (!paragraph) {
      return;
    }
    if (sentenceId) {
      // 单句子标记
      const sentence = paragraph.sc.find((item) => item.si === sentenceId);
      if (sentence) {
        sentence.mark = !sentence.mark;
        paragraph.mark = paragraph.sc.every((item) => item.mark);
      }
    } else {
      // 整段标记
      paragraph.mark = !paragraph.mark;
      paragraph.sc = paragraph.sc.map((sentence) => ({ ...sentence, mark: paragraph.mark }));
    }

    submitSrcSummary();
  },
  submitSrcSummary: async () => {
    try {
      const { newCompleteDoc } = get();
      if (!newCompleteDoc) {
        return;
      }
      const _newCompleteDoc = cloneDeep(newCompleteDoc);
      _newCompleteDoc.pg = _newCompleteDoc.pg.filter((item) => {
        return item.sc.every((sentence) => sentence.tc.trim() !== "");
      });
      // 为了防止请求耗时，先更新本地数据
      set({
        detailInfo: {
          ...get().detailInfo!,
          completeDoc: _newCompleteDoc,
        },
      });

      await editSrcContent({
        streamName: `live/${summaryCommonStore.getState().streamId}`,
        completeDoc: _newCompleteDoc,
      });
      await get().fetchDetailInfo({
        recordingType: "live",
        streamId: summaryCommonStore.getState().streamId,
      });
    } catch (error: any) {
      // 录制信息已删除
      if (error?.errCode === 40002103) {
        summaryCommonStore.getState().setErrorStatus(40002103);
        return;
      }
      console.error(error);
    }
  },
  modifyNewCompleteDoc: (newData) => {
    set({ newCompleteDoc: newData });
  },
  submitModifyTitle: async (newTitle) => {
    try {
      const streamId = summaryCommonStore.getState().streamId;
      if (!streamId) {
        throw new Error("streamId不存在");
      }
      await renameRecord(`live/${streamId}`, newTitle);
      set({ generatedTitle: newTitle });
      fridayReporter.pushData({
        eventCode: 'SummaryPage_Rename'
      })
    } catch (error) {
      console.error(error);
    }
  },
  setSrcLoading: (loading) => {
    set({ srcLoading: loading });
  },
  generateTitle: async (reload = false, sName) => {
    try {
      if (get().titleGenerating) return;
      set({ titleGenerating: true });
      const shareInfo = summaryCommonStore.getState().shareInfo;
      const useComplete = shareInfo?.origin === Origin.MEETING_PLATE && shareInfo?.fileTransEnable;
      const streamName = sName || `live/${summaryCommonStore.getState().streamId}`;
      const contentStep = useComplete ? "COMPLETE_DOC" : "SRC_DOC";
      let res;
      if (shareInfo && summaryCommonStore.getState().shareScope !== MeetingRecordVisibility.orgMember) {
        res = await getShareMeetingTitle(streamName, reload, contentStep);
      } else {
        res = await getMeetingTitle(streamName, reload, contentStep);
      }
      if (!res) {
        throw new Error("生成标题失败");
      }
      const title = res.data.title || res.data.srcTitleSummary || res.data.completeTitleSummary;
      set({ generatedTitle: title });
    } catch (error) {
      console.log("generate title error", error);
    } finally {
      set({ titleGenerating: false });
    }
  },
}));

export default summarySrcStore;
